/**
 * session 失效的通知管道。
 *
 * 解決的是一個跨層問題：**發現 session 過期的是資料層，但能處理它的是登入層。**
 * `apiFetch` 收到 401 時只能拋錯，各 app 的資料 hook 接到後把畫面換成「載入失敗
 * ＋重試」；而重試只會再打一次同樣的 API、再拿一次 401，使用者就卡在原地——
 * 唯一的出路是重新整理頁面，因為只有那樣才會重新經過 `AuthGate` 的登入檢查。
 *
 * 這裡讓 401 直接通知 `useSession`，由它把狀態翻回未登入，`AuthGate` 便會自動
 * 換上登入頁。各 app 不需要記得接這條線，也就不會有的 app 接了、有的忘了。
 *
 * 刻意不用 `window` 的 CustomEvent：這是同一份 JS bundle 內的模組間通訊，
 * 走全域事件反而讓來源難以追蹤，且在測試中得多準備一個 DOM。
 */

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * 訂閱 session 失效通知。
 *
 * @returns 取消訂閱
 */
export function onSessionExpired(listener: Listener): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/**
 * 廣播 session 已失效。
 *
 * 由 `apiFetch` 在收到 401 時呼叫。沒有訂閱者時什麼也不做——例如尚未掛載
 * `AuthGate` 的頁面，那裡本來就沒有需要被打斷的畫面。
 */
export function notifySessionExpired(): void {
  for (const listener of listeners) {
    listener();
  }
}
